import {
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
} from '@mui/material'
import { useState, useEffect, useMemo } from 'react'
import { Controller, FieldError } from 'react-hook-form'
import {
  CardFormat,
  ExternalSystemField,
  ExternalSystemFieldDataType,
  ExternalSystemFieldStatus,
} from 'shared/types'
import { Subtitle, Row } from './ExternalSystemFieldRow.style'

interface ExternalSystemFieldRowProps extends ExternalSystemField {
  onSave: (id: string, value: string) => void
  disabled?: boolean
  onChangeValue?: (id: string, value: string) => void
  fieldStatus?: ExternalSystemFieldStatus
  onFieldCanSaveChange?: (fieldId: string, canSave: boolean) => void
  onFieldValidityChange?: (fieldId: string, isValid: boolean) => void
  control?: any
  error?: FieldError
  name?: string
}

const ExternalSystemFieldRow = ({
  value,
  node: { id, title, summary, dataType, regexPattern },
  onSave,
  disabled,
  onChangeValue,
  fieldStatus = ExternalSystemFieldStatus.Idle,
  onFieldCanSaveChange,
  onFieldValidityChange,
  control,
  error,
  name,
}: ExternalSystemFieldRowProps) => {
  const [inputValue, setInputValue] = useState<string>(value || '')
  const [isValid, setIsValid] = useState(true)
  const validationRegex = useMemo(
    () => new RegExp(regexPattern),
    [regexPattern]
  )

  useEffect(() => {
    if (!control) {
      const val = value || ''
      setInputValue(val)
      if (regexPattern === '') {
        setIsValid(true)
      } else {
        const originalValue = value || ''
        if (originalValue === '') {
          setIsValid(true)
        } else {
          setIsValid(val !== '' && validationRegex.test(val))
        }
      }
    }
  }, [value, control, validationRegex, regexPattern])

  const handleManualValueChange = (newValue: string) => {
    setInputValue(newValue)
    onChangeValue && onChangeValue(id, newValue)

    if (regexPattern === '') {
      setIsValid(true)
      return
    }

    const originalValue = value || ''

    if (originalValue === '' && newValue === '') {
      setIsValid(true)
      return
    }

    if (newValue === '') {
      setIsValid(false)
    } else {
      setIsValid(validationRegex.test(newValue))
    }
  }

  const hasValueChanged = inputValue !== value && inputValue !== ''
  const isSaveDisabled =
    !hasValueChanged ||
    !isValid ||
    fieldStatus === ExternalSystemFieldStatus.Saving

  useEffect(() => {
    if (!control) {
      onFieldCanSaveChange?.(id, !isSaveDisabled)
      onFieldValidityChange?.(id, isValid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveDisabled, isValid, control])

  const renderField = (fieldProps: any) =>
    dataType === ExternalSystemFieldDataType.CardFormat ? (
      <Select
        {...fieldProps}
        fullWidth={true}
        error={!control ? !isValid : !!error}
        disabled={disabled || fieldStatus === ExternalSystemFieldStatus.Saving}
      >
        <MenuItem value="" disabled>
          <span>&nbsp;</span>
        </MenuItem>
        {(Object.values(CardFormat) as Array<keyof typeof CardFormat>).map(
          key => (
            <MenuItem value={key} key={key}>
              {key}
            </MenuItem>
          )
        )}
      </Select>
    ) : (
      <TextField
        {...fieldProps}
        fullWidth={true}
        error={!control ? !isValid : !!error}
        disabled={disabled || fieldStatus === ExternalSystemFieldStatus.Saving}
      />
    )

  return (
    <Row container>
      <Grid item md={4} xs={5}>
        <Typography>{title}</Typography>
        <Subtitle>{summary}</Subtitle>
      </Grid>
      <Grid item md={8} xs={7}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            {control && name ? (
              <Controller
                name={name}
                control={control}
                render={({ field }) => renderField(field)}
              />
            ) : (
              renderField({
                value: inputValue,
                onChange: (e: any) => handleManualValueChange(e.target.value),
              })
            )}
          </Box>
        </Box>
      </Grid>
    </Row>
  )
}

export default ExternalSystemFieldRow
